@import '../../../../style/src/typography/mixins';

:host {
	// Needed for additional padding calculations in JS
	--zds-input-content-start-inset-left: var(--zds-input-inset-x);
	--zds-input-content-end-inset-right: var(--zds-input-inset-x);

	display: block;
}

.zds-input-content-container {
	display: flex;
	position: relative;

	&:not(&--ready) {
		visibility: hidden;
	}

	&:not(&--disabled) {
		cursor: text;
	}

	&:not(&--plaintext) {
		background: var(--zds-input-background);
		border-radius: var(--zds-input-border-radius);
	}

	&--invalid {
		--zds-input-border-color: var(--zds-input-border-color-invalid);
		--zds-input-border-color-focus: var(--zds-input-border-color-invalid);
		--zds-input-box-shadow-focus: var(--zds-input-box-shadow-invalid);
	}

	&__content-start,
	&__content-end {
		--zds-button-border-radius: var(--zds-input-border-radius);

		align-items: center;
		background-image: linear-gradient(to bottom, transparent, rgb(0 0 0 / 3%));
		border: var(--zds-input-border-width) solid var(--zds-input-border-color);
		display: flex;

		&--button {
			border: 0;
		}
	}

	&__content-start {
		--zds-button-border-top-right-radius: 0;
		--zds-button-border-bottom-right-radius: 0;

		border-bottom-left-radius: var(--zds-input-border-radius);
		border-top-left-radius: var(--zds-input-border-radius);
		padding-left: var(--zds-input-content-start-inset-left);
		padding-right: var(--zds-space-3);

		&--button {
			padding: 0;
		}
	}

	&__content-end {
		--zds-button-border-top-left-radius: 0;
		--zds-button-border-bottom-left-radius: 0;

		border-bottom-right-radius: var(--zds-input-border-radius);
		border-top-right-radius: var(--zds-input-border-radius);
		padding-left: var(--zds-space-3);
		padding-right: var(--zds-input-content-end-inset-right);

		&--button {
			padding: 0;
		}
	}

	&--fit-height {
		height: 100%;
	}

	&--invalid &__content-start,
	&--invalid &__content-end {
		background: none;
		background-color: var(--zds-color-danger);
		color: white;

		::slotted(zds-icon) {
			color: var(--zds-color-danger-foreground) !important;
		}
	}

	&__input-wrapper {
		$input-wrapper: &;

		align-items: center;
		display: flex;
		flex-grow: 1;
		gap: calc(var(--zds-input-inset-x) / 2);
		max-width: 100%;
		width: var(--zds-input-width, auto); // Variable may be passed by parent components
		z-index: 1;

		&:not(&--plaintext) {
			border: var(--zds-input-border-width) solid var(--zds-input-border-color);
			border-radius: var(--zds-input-border-radius);
			padding-inline: var(--zds-input-inset-x);
			transition:
				background-color var(--zds-transition-sm),
				border-color var(--zds-transition-sm),
				box-shadow var(--zds-transition-sm); // Don't transition paddings

			&#{$input-wrapper}--instant-save {
				&:not(&#{$input-wrapper}--focused):hover {
					background-color: var(--zds-input-instant-save-background-hover);
					border-color: var(--zds-input-instant-save-border-color-hover);
				}

				&#{$input-wrapper}--focused {
					background-color: var(--zds-input-instant-save-background-focus);
				}
			}

			&#{$input-wrapper}--instant-save-no-border {
				border-color: transparent;
			}

			&#{$input-wrapper}--focused {
				border: var(--zds-input-border-width) solid var(--zds-input-border-color-focus);
				box-shadow: var(--zds-input-box-shadow-focus);
			}

			&#{$input-wrapper}--disabled,
			&#{$input-wrapper}--readonly {
				background-color: var(--zds-input-background-disabled);
			}

			&#{$input-wrapper}--has-content-start {
				border-bottom-left-radius: 0;
				border-left: 0;
				border-top-left-radius: 0;
			}

			&#{$input-wrapper}--has-content-end {
				border-bottom-right-radius: 0;
				border-right: 0;
				border-top-right-radius: 0;
			}

			&#{$input-wrapper}--hidden {
				display: none;
			}
		}
	}

	&__input {
		width: 100%;

		&--no-events {
			pointer-events: none;
		}
	}

	&__input-start,
	&__input-end {
		align-items: center;
		color: var(--zds-input-placeholder-color);
		display: flex;
		gap: var(--zds-space-1);

		::slotted(zds-icon[size='md']) {
			font-size: 22px;
		}
	}

	&__input-start {
		margin-left: calc(-1 * var(--zds-input-inset-x) / 2);
	}

	&__input-end {
		margin-right: calc(-1 * var(--zds-input-inset-x) / 2);
	}

	&__status-icon {
		color: var(--zds-color-danger);
		flex-shrink: 0;
	}

	&__instant-save-hover-icon {
		color: var(--zds-color-grey-600);
		display: flex;
		justify-content: center;
		width: 30px; // Matches clear button width for nice change from hover icon to clear button
	}

	&__input-wrapper--focused &__instant-save-hover-icon,
	&__input-wrapper:not(:hover) &__instant-save-hover-icon {
		// Hide instand-save hover button when not hovering and when focused
		display: none;
	}

	&__instant-save-buttons {
		display: flex;
		gap: var(--zds-space-1);
		position: absolute;
		right: 0;
		top: calc(100% + var(--zds-space-2));
	}

	&__instant-save-button {
		align-items: center;
		background-color: var(--zds-color-grey-25);
		border: 1px solid var(--zds-color-grey-100);
		border-radius: var(--zds-shape-radius-sm);
		box-shadow:
			0 2px 7px -1px rgb(38 50 56 / 27%),
			0 1px 3px -1px rgb(38 50 56 / 12%);
		color: var(--zds-color-grey-600);
		cursor: pointer;
		display: flex;
		height: 34px;
		justify-content: center;
		transition: var(--zds-transition-sm);
		width: 34px;
		z-index: 99;
	}

	&__instant-save-button:hover {
		border-color: transparent;
	}

	&:not(&--focused) &__instant-save-button {
		display: none;
	}

	&__instant-save-button--reject:hover {
		background-color: var(--zds-color-danger);
		color: var(--zds-color-danger-foreground);
	}

	&__instant-save-button--accept:hover {
		background-color: var(--zds-color-success);
		color: var(--zds-color-success-foreground);
	}
}
